import { SelectBox } from "devextreme-react/select-box";
import { RequiredRule, Validator } from "devextreme-react/validator";
import { useContext } from 'react';
import { useGetLookupValuesByType } from '../../../../api/lookup';
import { useGetCompanyUsers } from '../../../../api/users';
import { DateWithTimeframeBox } from '../../../../components';
import { useAuth } from '../../../../contexts/auth';
import { Field, Panel } from '../../../../layouts';
import { AR, OrderStatus, ScriptFileType } from '../../../../types/enums';
import { formatDateWithTimeframe, formatShortDate, TimeframeType } from '../../../../utils/date-utils';
import { RefetchScope, RetailOrderContext } from '../context/RetailOrderContext';
import CallScriptComp from "../../call-script/component/CallScriptComp";

export default function ScheduleCountertopDates() {

	const { order, modifyOrder, refetchOrder } = useContext(RetailOrderContext);

	const isOrderReadonly =
		order.details.orderStatusID === OrderStatus.Closed ||
		order.details.orderStatusID === OrderStatus.Cancelled ||
		order.details.orderStatusID === OrderStatus.Pending;

	let dateRequired = order.details.activeARs.includes(AR.CfiToEnterScheduledInstallDate);

	let dateReadOnly = !order.details.activeARs.includes(AR.CfiToEnterScheduledInstallDate) &&
		!order.details.activeARs.includes(AR.CfiToPrintPackingSlip) &&
		!order.details.activeARs.includes(AR.CfiToEnterActualInstallDate);

	let installerReadOnly = dateReadOnly;
	let rescheduleReasonReadonly = dateReadOnly;
	let rescheduleReasonRequired = !rescheduleReasonReadonly &&
		((!!order.details.scheduledInstallDate && order.details.scheduledInstallDate !== order.originalDetails.scheduledInstallDate && !order.details.isDeliveryOnly) ||
			(!!order.details.scheduledDeliveryDate && order.details.scheduledDeliveryDate !== order.originalDetails.scheduledDeliveryDate && order.details.isDeliveryOnly));

	const { user } = useAuth();
	const companyUsers = useGetCompanyUsers(user?.companyId!);
	const reschedulReasons = useGetLookupValuesByType('RescheduleReason');

	return (
		<>
			<Field label="Measure Date" visible={!order.details.isDeliveryOnly}>
				{formatDateWithTimeframe(order.details.measureDate, TimeframeType.Short)}
			</Field>
			<Field label="Template Date" visible={!order.details.isDeliveryOnly}>
				{formatDateWithTimeframe(order.details.enterTemplateDate, TimeframeType.Short)}
			</Field>
			<Field label={'Cabinet Install Date'}>
				{formatShortDate(order.details.cabinetDate)}
			</Field>
			<Field label="Scheduled Install Date"
				visible={!order.details.isDeliveryOnly}>
				<DateWithTimeframeBox elementAttr={{ 'data-testid': 'dttb-scheduled-install-date' }}
					value={order.details.scheduledInstallDate}
					timeframeType={TimeframeType.Long}
					timeRequired={true}
					onValueChanged={(e) => modifyOrder({ ...order.details, scheduledInstallDate: e.value })}
					readOnly={dateReadOnly || isOrderReadonly}
					className={dateRequired ? 'required' : ''}
					min={!dateReadOnly ? new Date() : undefined}>
					<Validator>
						{dateRequired && order.modified &&
							<RequiredRule message={'Scheduled Install Date is required'} />}
					</Validator>
				</DateWithTimeframeBox>
				{
					(order.details.activeARs.includes(AR.CfiToEnterScheduledInstallDate) || order.details.resolvedARs.includes(AR.CfiToEnterScheduledInstallDate)) && !order.details.isDeliveryOnly &&
					<CallScriptComp
						retailOrderID={order.details.retailOrderID}
						retailOrderNumber={order.details.roNumber}
						fileType={ScriptFileType.InstallChecklist}
						fileName={"Install Checklist"}
						fileAttachment={order.details.files}
						submitCallback={() => refetchOrder(RefetchScope.Files)}
					/>
				}
			</Field>
			<Field label="Scheduled Delivery Date"
				visible={order.details.isDeliveryOnly}>
				<DateWithTimeframeBox value={order.details.scheduledDeliveryDate}
					timeframeType={TimeframeType.Long}
					timeRequired={true}
					onValueChanged={(e) => modifyOrder({ ...order.details, scheduledDeliveryDate: e.value })}
					readOnly={dateReadOnly || isOrderReadonly}
					className={dateRequired ? 'required' : ''}
					min={!dateReadOnly ? new Date() : undefined}>
					<Validator>
						{dateRequired && order.modified &&
							<RequiredRule message={'Scheduled Delivery Date is required'} />}
					</Validator>
				</DateWithTimeframeBox>
			</Field>
			<Field label={'Installer'}
				visible={!order.details.isDeliveryOnly}>
				<SelectBox elementAttr={{ 'data-testid': 'dd-installer' }}
					value={order.details.installer}
					showClearButton={true}
					readOnly={installerReadOnly || isOrderReadonly}
					valueExpr={'userName'}
					displayExpr={'fullName'}
					onValueChanged={(e) => modifyOrder({ ...order.details, installer: e.value })}
					dataSource={companyUsers?.data.filter(x => x.groups?.includes('Installer') &&
						(!x.inactive || x.userName === order.details.installer))} />
			</Field>
			<Field label={'Delivery By'}
				visible={order.details.isDeliveryOnly}>
				<SelectBox elementAttr={{ 'data-testid': 'dd-deliveryby' }}
					value={order.details.deliveryBy}
					showClearButton={true}
					readOnly={installerReadOnly || isOrderReadonly}
					valueExpr={'userName'}
					displayExpr={'fullName'}
					onValueChanged={(e) => modifyOrder({ ...order.details, deliveryBy: e.value })}
					dataSource={companyUsers?.data.filter(x => x.groups?.includes('Delivery') &&
						(!x.inactive || x.userName === order.details.deliveryBy))} />
			</Field>
			<Field label={'Reschedule Reason'}
				visible={((!!order.originalDetails.scheduledInstallDate && !order.details.isDeliveryOnly) ||
					(!!order.originalDetails.scheduledDeliveryDate && order.details.isDeliveryOnly)) &&
					(!rescheduleReasonReadonly || !!order.details.rescheduleReasonID)}>
				<SelectBox
					value={order.details.rescheduleReasonID}
					showClearButton={true}
					readOnly={rescheduleReasonReadonly || isOrderReadonly}
					onValueChanged={(e) => modifyOrder({ ...order.details, rescheduleReasonID: e.value })}
					dataSource={reschedulReasons.data}
					valueExpr={'lookupID'}
					displayExpr={'displayValue'}
					className={rescheduleReasonRequired ? 'required' : ''}>
					<Validator>
						{rescheduleReasonRequired && order.modified &&
							<RequiredRule message={'Reschedule Reason is required'} />}
					</Validator>
				</SelectBox>
			</Field>
		</>
	);
}
